import { motion } from "motion/react";
import SEOHead from "@/components/SEOHead";
import FeatureTabs from "@/components/features/FeatureTabs";
import ArchitectureSequence from "@/components/features/ArchitectureSequence";

const EASE = [0.25, 0.1, 0.25, 1] as const;
const CONTAINER = "max-w-7xl mx-auto px-6 lg:px-16";
const EYEBROW = "text-xs font-medium tracking-[0.2em] uppercase mb-5";

const Features = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <SEOHead
      title="Features - Lumient"
      description="Operational state for document-heavy work. Scout, Versioned Workflows, and Live Records keep workflow logic, source evidence, and queryable records connected."
      canonical="https://lumient.ai/features"
    />

    <main className="flex-1 w-full">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-background border-b border-border">
        <div className={`${CONTAINER} pt-20 pb-10 lg:pt-28 lg:pb-14`}>
          <motion.div
            className="grid grid-cols-12 items-start gap-y-8"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
          >
            {/* Heading — 7 cols */}
            <div className="col-span-12 lg:col-span-7">
              <motion.p
                className={EYEBROW}
                style={{ color: "oklch(var(--primary-readable))" }}
                variants={{ hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }}
              >
                Features
              </motion.p>
              <motion.h1
                className="font-serif font-semibold text-[clamp(3rem,6vw,5.75rem)] leading-[0.95] tracking-[-0.04em] text-[#0B0B0B]"
                variants={{ hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } }}
              >
                Operational state for<br />
                <span style={{ color: "oklch(var(--primary))" }}>document-heavy work.</span>
              </motion.h1>
            </div>

            {/* Subcopy — 5 cols */}
            <motion.p
              className="col-span-12 lg:col-span-5 text-[1.05rem] font-light leading-[1.65] text-muted-foreground lg:pt-12"
              variants={{ hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } } }}
            >
              Connect the operational evidence your team already works from: contracts, invoices,
              forms, reports, spreadsheets, and system exports. Lumient turns it into a governed
              operating layer: workflow logic, traceable evidence, and queryable history for every
              domain you manage.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Primary platform surfaces ──────────────────────────────────────── */}
      <FeatureTabs />

      {/* ── How it works ───────────────────────────────────────────────────── */}
      <ArchitectureSequence />

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="bg-surface-inverse">
        <div className={`${CONTAINER} py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8`}>
          <div>
            <h2 className="text-3xl font-normal text-surface-inverse-foreground tracking-tight mb-2">
              Show us the records your team rebuilds by hand.
            </h2>
            <p className="text-surface-inverse-muted text-base">
              We will map the documents, workflow logic, evidence, and policies your team needs to keep current.
            </p>
          </div>
          <a
            href="/contact"
            className="glow-button flex-shrink-0 px-7 py-3 text-sm font-semibold rounded-full whitespace-nowrap"
          >
            Book a Demo
          </a>
        </div>
      </section>

    </main>
  </div>
);

export default Features;
